/**
 * Public home (`/`): craft landing for logged-out / still-restoring session.
 * After auth is known valid → AppLoadingScreen then client-side nav to desk.
 *
 * Do not gate `/` on AuthPendingScreen — that hid the landing craft floor
 * during session restore and made the public page feel like a blank auth shell.
 * Login routes keep AuthPending; this page does not.
 *
 * @see shared/layouts/components/landing/LandingPage.tsx
 * @see shared/components/feedback/AppLoadingScreen.tsx
 */

'use client'

import React, { useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useAuth } from '@/domains/auth'
import { LandingPage } from '@/shared/layouts/components/landing/LandingPage'
import { AppLoadingScreen } from '@/shared/components/feedback/AppLoadingScreen'

export default function HomePage() {
  const router = useRouter()
  const { isInitialized, isAuthenticated, user } = useAuth()

  useEffect(() => {
    if (!isInitialized || !isAuthenticated || !user) return

    let userType: string | null = null
    try {
      userType =
        sessionStorage.getItem('userType') ||
        localStorage.getItem('userType')
    } catch {
      /* no-op */
    }
    const isClient =
      userType === 'client' ||
      Boolean((user as { clientData?: unknown }).clientData) ||
      (Array.isArray((user as { roles?: string[] }).roles) &&
        (user as { roles?: string[] }).roles!.includes('client'))
    const target = isClient ? '/client/dashboard' : '/dashboard'
    router.replace(target)
  }, [isInitialized, isAuthenticated, user, router])

  // Known session: hand off to workspace (not public marketing shell)
  if (isInitialized && isAuthenticated && user) {
    return (
      <AppLoadingScreen message="Opening your workspace…" variant="fullscreen" size="lg" />
    )
  }

  // Public craft floor: logged out, or session still restoring (optimistic)
  return <LandingPage />
}
